import { List } from "../../@types/list";
import {
  AnsweringClient,
  getClientsParams,
  GetManagedInstrumentsSearchParams,
  IInstrument,
  IWalletSummary,
} from "../../clients/answeringClient";

interface LicenseAssignmentItem {
  instrumentId: number;
  quantity: number;
}

export interface IResellerClient {
  clientId: number;
  clientUid: string;
  clientName: string;
  clientEmail: string;
  linkedAt: Date;
  isPsychologist: boolean;
  origin?: ResellerClientOrigin;
  invitedAt?: Date | null;
  acceptedAt?: Date | null;
  invitationStatus?: InvitationStatus;
}

export interface RequestPsychologistPromotionParams {
  clientUid: string;
  cpf: string;
  crp?: string;
}

export type ResellerClientOrigin = "EXISTING_USER_LINKED" | "INVITED_NEW_USER";
export type InvitationStatus = "PENDING" | "ACCEPTED";
export interface ResellerClientLinkResponse {
  clientId: number;
  clientUid: string;
  clientName: string;
  clientEmail: string;
  linkedAt: string;
  origin?: ResellerClientOrigin;
  invitationType?: ResellerClientOrigin;
}

export class ResellerManagementController {
  private client = new AnsweringClient();

  private asBoolean(value: unknown): boolean {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return normalized === "true" || normalized === "t" || normalized === "1";
    }
    return false;
  }

  /** busca e retorna List<IResellerClient> com linkedAt convertido */
  public async getResellerClients(
    token: string,
    params: getClientsParams
  ): Promise<List<IResellerClient>> {
    const resp = await this.client.listResellerClients(token, params);
    const apiList: List<any> = resp.data;

    const list: List<IResellerClient> = {
      ...apiList,
      data: apiList.data.map((c: any) => ({
        ...c,
        isPsychologist: this.asBoolean(c?.isPsychologist),
        linkedAt: new Date(c.linkedAt),
        invitedAt: c.invitedAt ? new Date(c.invitedAt) : null,
        acceptedAt: c.acceptedAt ? new Date(c.acceptedAt) : null,
      })),
    };

    return list;
  }

  /** saldo da carteira do revendedor logado (em licenças; pode ser negativo) */
  public async getWalletSummary(token: string): Promise<IWalletSummary> {
    const resp = await this.client.getResellerWalletSummary(token);
    return resp.data;
  }

  /** busca e retorna array de IInstrument */
  public async getManagedInstruments(
    token: string,
    params: GetManagedInstrumentsSearchParams
  ): Promise<IInstrument[]> {
    const resp = await this.client.searchResellerInstruments(token, params);
    return resp.data.data;
  }

  public async addOrInviteResellerClient(
    token: string,
    data: any
  ): Promise<ResellerClientLinkResponse> {
    const response = await this.client.addOrInviteResellerClient(token, data);
    return response.data as ResellerClientLinkResponse;
  }

  public async assignResellerLicenses(
    token: string,
    clientId: number,
    items: LicenseAssignmentItem[]
  ): Promise<void> {
    await this.client.assignResellerLicenses(token, clientId, items);
  }

  public async requestPsychologistPromotion(
    token: string,
    params: RequestPsychologistPromotionParams
  ): Promise<void> {
    await this.client.requestPsychologistPromotion(token, params);
  }

  public async resendResellerInvitation(
    token: string,
    clientUid: string
  ): Promise<void> {
    await this.client.resendResellerInvitation(token, clientUid);
  }

  public async revokeResellerLicenses(
    token: string,
    distributedLicenseId: number,
    quantityToRevoke: number
  ): Promise<void> {
    await this.client.revokeResellerLicenses(
      token,
      distributedLicenseId,
      quantityToRevoke
    );
  }
}
